import {
  BlockerContext,
  Form,
  FormField,
  FormSubmitHandler,
  NavigateWithoutBlocker,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isFalse} from 'ramda-adjunct';

import {QrCodeSettingsResponseBody} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';
import {useBranches} from '@dms/shared';

import {NullishBoolean, RequiredTestIdProps, suffixTestId} from 'shared/web';

const PAYMENT_METHOD_OPTIONS = [
  {value: 'bankTransfer', label: i18n.t('entity.invoice.paymentMethod.bankTransfer')},
  {value: 'cash', label: i18n.t('entity.invoice.paymentMethod.cash')},
  {value: 'card', label: i18n.t('entity.invoice.paymentMethod.card')},
  {value: 'offset', label: i18n.t('entity.invoice.paymentMethod.offset')},
  {value: 'freeOfCharge', label: i18n.t('entity.invoice.paymentMethod.freeOfCharge')},
  {value: 'cashOnDelivery', label: i18n.t('entity.invoice.paymentMethod.cashOnDelivery')},
];

const getQrCodeSettingsSchema = (isSystem: NullishBoolean) => {
  const branchIdSchema = isFalse(isSystem) ? z.string().nonempty() : z.string().nullable();

  return z.object({
    branchId: branchIdSchema,
    paymentMethods: z.array(z.string()),
    isSystem: z.boolean(),
  });
};

type QrCodeSettingsFormData = z.infer<ReturnType<typeof getQrCodeSettingsSchema>>;

type QrCodeSettingsApiData = Omit<QrCodeSettingsResponseBody, 'id'>;

export interface QrCodeSettingsDetailFormProps extends RequiredTestIdProps {
  defaultValues: QrCodeSettingsApiData;
  isEdit: boolean;
  onSubmit: (values: QrCodeSettingsApiData, blockerContext?: BlockerContext) => Promise<void>;
  onCancel: (navigateWithoutBlocker: NavigateWithoutBlocker) => void;
}

function transformApiToForm(apiData: QrCodeSettingsApiData): QrCodeSettingsFormData {
  const paymentMethods: string[] = [];
  if (apiData.bankTransfer) {
    paymentMethods.push('bankTransfer');
  }
  if (apiData.cash) {
    paymentMethods.push('cash');
  }
  if (apiData.card) {
    paymentMethods.push('card');
  }
  if (apiData.offset) {
    paymentMethods.push('offset');
  }
  if (apiData.freeOfCharge) {
    paymentMethods.push('freeOfCharge');
  }
  if (apiData.cashOnDelivery) {
    paymentMethods.push('cashOnDelivery');
  }

  return {
    branchId: apiData.branchId,
    paymentMethods,
    isSystem: apiData.isSystem,
  };
}

function transformFormToApi(formData: QrCodeSettingsFormData): QrCodeSettingsApiData {
  return {
    branchId: formData.branchId,
    bankTransfer: formData.paymentMethods.includes('bankTransfer'),
    cash: formData.paymentMethods.includes('cash'),
    card: formData.paymentMethods.includes('card'),
    offset: formData.paymentMethods.includes('offset'),
    freeOfCharge: formData.paymentMethods.includes('freeOfCharge'),
    cashOnDelivery: formData.paymentMethods.includes('cashOnDelivery'),
    isSystem: formData.isSystem,
  };
}

export function QrCodeSettingsDetailForm(props: QrCodeSettingsDetailFormProps) {
  const {branchOptions} = useBranches();

  const handleSubmit: FormSubmitHandler<QrCodeSettingsFormData> = async (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await props.onSubmit(transformFormToApi(values), blockerContext);
  };

  return (
    <Form
      onSubmit={handleSubmit}
      defaultValues={transformApiToForm(props.defaultValues)}
      experimentalZodSchema={getQrCodeSettingsSchema(props.defaultValues.isSystem)}
      shouldWatchForUnsavedChanges
      data-testid={suffixTestId('form', props)}
    >
      {(control, _formApi, navigateWithoutBlocker) => (
        <VStack spacing={4}>
          <Grid columns={2}>
            <FormField
              name="branchId"
              type="choice"
              control={control}
              label={i18n.t('entity.branch.labels.branch')}
              isDisabled={props.defaultValues.isSystem}
              options={branchOptions}
              isRequired={isFalse(props.defaultValues.isSystem)}
              placeholder={
                props.defaultValues.isSystem
                  ? i18n.t('page.accounting.labels.tenantDefinition')
                  : undefined
              }
              data-testid={suffixTestId('branchId', props)}
            />
          </Grid>

          <FormField
            name="paymentMethods"
            type="checkboxes"
            control={control}
            options={PAYMENT_METHOD_OPTIONS}
            direction="vertical"
            data-testid={suffixTestId('paymentMethods', props)}
          />

          <SettingsFooter
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: i18n.t('general.actions.cancel'),
                onClick: () => props.onCancel(navigateWithoutBlocker),
                'data-testid': suffixTestId('cancelButton', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                title: props.isEdit
                  ? i18n.t('general.actions.save')
                  : i18n.t('general.actions.create'),
                'data-testid': suffixTestId('submitButton', props),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
